import { supabase } from '../lib/supabase';

// Business directory service for Holistic Directories
export const businessService = {
  // Get all active businesses with pagination
  async getBusinesses({ 
    page = 1, 
    limit = 12, 
    categoryId = null, 
    searchQuery = '', 
    location = '', 
    sortBy = 'created_at',
    sortOrder = 'desc'
  } = {}) {
    try {
      // Validate sortBy parameter to prevent database errors
      const validSortFields = [
        'created_at', 
        'updated_at', 
        'name', 
        'average_rating', 
        'total_reviews', 
        'total_views',
        'is_featured'
      ];
      
      // Use default sorting if invalid sortBy is provided
      const safeSortBy = validSortFields?.includes(sortBy) ? sortBy : 'created_at';

      let query = supabase
        ?.from('businesses')
        ?.select(`
          id,
          name,
          short_description,
          description,
          logo_url,
          cover_image_url,
          email,
          phone,
          website_url,
          address_line1,
          address_line2,
          city,
          county,
          postcode,
          country,
          latitude,
          longitude,
          average_rating,
          total_reviews,
          total_views,
          is_featured,
          status,
          verified_at,
          created_at,
          updated_at,
          google_rating,
          google_review_count,
          google_my_business_url,
          categories (
            id,
            name,
            slug
          ),
          business_hours (
            id,
            day_of_week,
            open_time,
            close_time,
            is_closed
          )
        `)
        ?.eq('status', 'active');

      // Apply category filter
      if (categoryId) {
        query = query?.eq('category_id', categoryId);
      }

      // Apply search filter
      if (searchQuery) {
        query = query?.or(`name.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%,short_description.ilike.%${searchQuery}%`);
      }

      // Apply location filter
      if (location) {
        query = query?.or(`city.ilike.%${location}%,county.ilike.%${location}%,postcode.ilike.%${location}%`);
      }

      // Apply safe sorting - only use validated field names
      query = query?.order(safeSortBy, { ascending: sortOrder === 'asc' });

      // Apply pagination
      const from = (page - 1) * limit;
      const to = from + limit - 1;
      query = query?.range(from, to);

      const { data, error, count } = await query;

      if (error) {
        console.error('Business query error:', error);
        return { data: [], error: error?.message, totalCount: 0 };
      }

      return { 
        data: data || [], 
        error: null, 
        totalCount: count,
        hasMore: data?.length === limit
      };
    } catch (error) {
      console.error('Business service error:', error);
      const errorMessage = error?.message?.includes('Failed to fetch')
        ? 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' :'Failed to load businesses. Please try again.';
      
      return { data: [], error: errorMessage, totalCount: 0 };
    }
  },

  // Get all active businesses without pagination for homepage directory
  async getAllBusinesses({ 
    categoryId = null, 
    searchQuery = '', 
    location = '', 
    sortBy = 'created_at',
    sortOrder = 'desc',
    limit = null  // Optional limit for performance
  } = {}) {
    try {
      // Validate sortBy parameter to prevent database errors
      const validSortFields = [
        'created_at', 
        'updated_at', 
        'name', 
        'average_rating', 
        'total_reviews', 
        'total_views',
        'is_featured'
      ];
      
      // Use default sorting if invalid sortBy is provided
      const safeSortBy = validSortFields?.includes(sortBy) ? sortBy : 'created_at';

      let query = supabase
        ?.from('businesses')
        ?.select(`
          id,
          name,
          short_description,
          description,
          logo_url,
          cover_image_url,
          email,
          phone,
          website_url,
          address_line1,
          address_line2,
          city,
          county,
          postcode,
          country,
          latitude,
          longitude,
          average_rating,
          total_reviews,
          total_views,
          is_featured,
          status,
          verified_at,
          created_at,
          updated_at,
          google_rating,
          google_review_count,
          google_my_business_url,
          categories (
            id,
            name,
            slug
          ),
          business_hours (
            id,
            day_of_week,
            open_time,
            close_time,
            is_closed
          ),
          business_images!inner (
            id,
            image_url,
            alt_text,
            display_order,
            is_featured
          )
        `, { count: 'exact' }) // Added count: 'exact' to get total count
        ?.eq('status', 'active');

      // Apply category filter
      if (categoryId) {
        query = query?.eq('category_id', categoryId);
      }

      // Apply search filter
      if (searchQuery) {
        query = query?.or(`name.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%,short_description.ilike.%${searchQuery}%`);
      }

      // Apply location filter
      if (location) {
        query = query?.or(`city.ilike.%${location}%,county.ilike.%${location}%,postcode.ilike.%${location}%`);
      }

      // Apply safe sorting - only use validated field names
      query = query?.order(safeSortBy, { ascending: sortOrder === 'asc' });

      // Apply optional limit for performance (e.g., show first 100 businesses)
      if (limit) {
        query = query?.limit(limit);
      }

      const { data, error, count } = await query;

      if (error) {
        console.error('All businesses query error:', error);
        return { data: [], error: error?.message, totalCount: 0 };
      }

      // Transform the data to provide better image handling and category formatting
      const transformedData = data?.map(business => ({
        ...business,
        // Provide primary image from multiple sources with proper fallback
        image: business?.business_images?.[0]?.image_url || business?.cover_image_url || business?.logo_url || null,
        businessLogo: business?.logo_url,
        // Format location for display
        location: [business?.city, business?.county]?.filter(Boolean)?.join(', ') || 'Location not specified',
        // Format categories as array of names for ListingCard
        categories: business?.categories ? [business?.categories?.name] : [],
        // Add rating information from Google My Business
        rating: business?.google_rating || business?.average_rating || null,
        reviewCount: business?.google_review_count || business?.total_reviews || 0,
        // Additional business information
        businessName: business?.name,
        nextAvailable: null, // Could be enhanced with actual availability data
        distance: null, // Could be calculated based on user location
        isFavorited: false, // Will be updated in the homepage component
        // Google My Business integration
        googleBusinessUrl: business?.google_my_business_url
      }));

      // Debug logging to help troubleshoot
      console.log('Business query results:', {
        dataCount: transformedData?.length || 0,
        totalCount: count || 0,
        hasData: !!transformedData?.length,
        firstBusiness: transformedData?.[0]?.name || 'No businesses found',
        firstBusinessImage: transformedData?.[0]?.image || 'No image'
      });

      return { 
        data: transformedData || [], 
        error: null, 
        totalCount: count || transformedData?.length || 0,
        hasMore: false // No pagination for all businesses
      };
    } catch (error) {
      console.error('All businesses service error:', error);
      const errorMessage = error?.message?.includes('Failed to fetch')
        ? 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' : 'Failed to load business directory. Please try again.';
      
      return { data: [], error: errorMessage, totalCount: 0 };
    }
  },

  // Enhanced getBusinessById to include all related data
  async getBusinessById(businessId) {
    try {
      const { data, error } = await supabase
        ?.from('businesses')
        ?.select(`
          id,
          name,
          short_description,
          description,
          logo_url,
          cover_image_url,
          email,
          phone,
          website_url,
          address_line1,
          address_line2,
          city,
          county,
          postcode,
          country,
          latitude,
          longitude,
          average_rating,
          total_reviews,
          total_views,
          is_featured,
          status,
          verified_at,
          created_at,
          updated_at,
          categories (
            id,
            name,
            slug,
            description,
            icon_url
          ),
          business_hours (
            id,
            day_of_week,
            open_time,
            close_time,
            is_closed
          ),
          business_images (
            id,
            image_url,
            alt_text,
            display_order,
            is_featured
          ),
          business_specializations (
            id,
            name,
            type
          ),
          owner:user_profiles (
            id,
            full_name,
            avatar_url
          )
        `)
        ?.eq('id', businessId)
        ?.single();

      if (error) {
        return { data: null, error: error?.message };
      }

      // Increment view count
      if (data) {
        await this.incrementViewCount(businessId);
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: 'Failed to load business details.' };
    }
  },

  // Get featured businesses
  async getFeaturedBusinesses(limit = 6) {
    try {
      const { data, error } = await supabase
        ?.from('businesses')
        ?.select(`
          id,
          name,
          short_description,
          logo_url,
          cover_image_url,
          city,
          county,
          average_rating,
          total_reviews,
          is_featured,
          categories (
            id,
            name,
            slug
          )
        `)
        ?.eq('status', 'active')
        ?.eq('is_featured', true)
        ?.order('created_at', { ascending: false })
        ?.limit(limit);

      if (error) {
        return { data: [], error: error?.message };
      }

      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: 'Failed to load featured businesses.' };
    }
  },

  // Create new business - keeping for backward compatibility
  async createBusiness(businessData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: 'You must be logged in to create a business.' };
      }

      // Check if user already has a business listing
      const { data: existingBusinesses, error: checkError } = await supabase
        ?.from('businesses')
        ?.select('id')
        ?.eq('owner_id', user?.id)
        ?.limit(1);

      if (checkError) {
        return { data: null, error: 'Failed to check existing businesses.' };
      }

      if (existingBusinesses?.length > 0) {
        return { data: null, error: 'You can only create one business listing per account. Please edit your existing business instead.' };
      }

      const { data, error } = await supabase
        ?.from('businesses')
        ?.insert({
          ...businessData,
          owner_id: user?.id,
          status: 'active', // Auto-approve all businesses
          created_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        })
        ?.select()
        ?.single();

      if (error) {
        return { data: null, error: error?.message };
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: 'Failed to create business. Please try again.' };
    }
  },

  // Update business
  async updateBusiness(businessId, updates) {
    try {
      const { data, error } = await supabase
        ?.from('businesses')
        ?.update({
          ...updates,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', businessId)
        ?.select()
        ?.single();

      if (error) {
        return { data: null, error: error?.message };
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: 'Failed to update business.' };
    }
  },

  // Delete business
  async deleteBusiness(businessId) {
    try {
      const { error } = await supabase
        ?.from('businesses')
        ?.delete()
        ?.eq('id', businessId);

      if (error) {
        return { error: error?.message };
      }

      return { error: null };
    } catch (error) {
      return { error: 'Failed to delete business.' };
    }
  },

  // Increment view count
  async incrementViewCount(businessId) {
    try {
      const { error } = await supabase?.rpc('increment_business_views', {
        business_uuid: businessId
      });

      if (error) {
        console.error('Failed to increment view count:', error);
      }
    } catch (error) {
      console.error('Failed to increment view count:', error);
    }
  },

  // Get businesses by owner
  async getBusinessesByOwner(ownerId) {
    try {
      const { data, error } = await supabase
        ?.from('businesses')
        ?.select(`
          id,
          name,
          short_description,
          logo_url,
          status,
          average_rating,
          total_reviews,
          total_views,
          created_at,
          updated_at,
          categories (
            id,
            name,
            slug
          )
        `)
        ?.eq('owner_id', ownerId)
        ?.order('created_at', { ascending: false });

      if (error) {
        return { data: [], error: error?.message };
      }

      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: 'Failed to load user businesses.' };
    }
  },

  // Get all businesses by owner (including pending for owners to see their own submissions)
  async getOwnBusinesses(ownerId) {
    try {
      const { data, error } = await supabase
        ?.from('businesses')
        ?.select(`
          id,
          name,
          short_description,
          description,
          logo_url,
          cover_image_url,
          email,
          phone,
          website_url,
          address_line1,
          address_line2,
          city,
          county,
          postcode,
          country,
          latitude,
          longitude,
          average_rating,
          total_reviews,
          total_views,
          is_featured,
          status,
          verified_at,
          created_at,
          updated_at,
          categories (
            id,
            name,
            slug
          ),
          business_hours (
            id,
            day_of_week,
            open_time,
            close_time,
            is_closed
          ),
          business_images (
            id,
            image_url,
            alt_text,
            display_order,
            is_featured
          ),
          business_specializations (
            id,
            name,
            type
          )
        `)
        ?.eq('owner_id', ownerId)
        ?.order('created_at', { ascending: false });

      if (error) {
        return { data: [], error: error?.message };
      }

      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: 'Failed to load your businesses.' };
    }
  },

  // Add new service management methods for the editable business listing
  getBusinessServices: async (businessId) => {
    try {
      if (!businessId) {
        return { data: [], error: 'Business ID is required' };
      }

      const { data, error } = await supabase
        ?.from('services')
        ?.select('*')
        ?.eq('business_id', businessId)
        ?.eq('is_active', true)
        ?.order('service_order', { ascending: true });

      return { data, error };
    } catch (error) {
      console.error('Error fetching business services:', error);
      return { data: [], error };
    }
  },

  createService: async (businessId, serviceData) => {
    try {
      const { data, error } = await supabase
        ?.from('services')
        ?.insert({
          business_id: businessId,
          name: serviceData?.name,
          description: serviceData?.description,
          price: serviceData?.price,
          duration_minutes: serviceData?.duration_minutes,
          is_in_person: serviceData?.is_in_person ?? true,
          is_online: serviceData?.is_online ?? false,
          max_participants: serviceData?.max_participants || 1,
          is_active: true
        })
        ?.select()
        ?.single();

      return { data, error };
    } catch (error) {
      console.error('Error creating service:', error);
      return { data: null, error };
    }
  },

  updateService: async (serviceId, serviceData) => {
    try {
      const { data, error } = await supabase
        ?.from('services')
        ?.update({
          name: serviceData?.name,
          description: serviceData?.description,
          price: serviceData?.price,
          duration_minutes: serviceData?.duration_minutes,
          is_in_person: serviceData?.is_in_person,
          is_online: serviceData?.is_online,
          max_participants: serviceData?.max_participants,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', serviceId)
        ?.select()
        ?.single();

      return { data, error };
    } catch (error) {
      console.error('Error updating service:', error);
      return { data: null, error };
    }
  },

  deleteService: async (serviceId) => {
    try {
      const { error } = await supabase
        ?.from('services')
        ?.update({ is_active: false })
        ?.eq('id', serviceId);

      return { error };
    } catch (error) {
      console.error('Error deleting service:', error);
      return { error };
    }
  },

  // Business hours management methods
  getBusinessHours: async (businessId) => {
    try {
      if (!businessId) {
        return { data: [], error: 'Business ID is required' };
      }

      const { data, error } = await supabase
        ?.from('business_hours')
        ?.select('*')
        ?.eq('business_id', businessId)
        ?.order('day_of_week', { ascending: true });

      return { data, error };
    } catch (error) {
      console.error('Error fetching business hours:', error);
      return { data: [], error };
    }
  },

  upsertBusinessHours: async (businessId, hoursData) => {
    try {
      const { error } = await supabase
        ?.from('business_hours')
        ?.upsert(
          hoursData?.map(hour => ({
            business_id: businessId,
            day_of_week: hour?.day_of_week,
            open_time: hour?.open_time,
            close_time: hour?.close_time,
            is_closed: hour?.is_closed
          })),
          { onConflict: 'business_id,day_of_week' }
        );

      return { error };
    } catch (error) {
      console.error('Error updating business hours:', error);
      return { error };
    }
  },

  updateBusinessLocation: async (businessId, locationData) => {
    try {
      const { data, error } = await supabase
        ?.from('businesses')
        ?.update({
          address_line1: locationData?.address_line1,
          address_line2: locationData?.address_line2,
          city: locationData?.city,
          county: locationData?.county,
          postcode: locationData?.postcode,
          country: locationData?.country,
          latitude: locationData?.latitude,
          longitude: locationData?.longitude,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', businessId)
        ?.select()
        ?.single();

      return { data, error };
    } catch (error) {
      console.error('Error updating business location:', error);
      return { data: null, error };
    }
  }
};

export default businessService;